import { isNil } from "@sunrise/utils";
import { disableAnimationsAtom } from "@sunrise/yallo-settings";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { ReactElement, memo, useEffect, useRef } from "react";

import { programBoxSize, recommendationRowSize } from "@/config/size";
import { SCREEN_WIDTH_IN_PX } from "@/core";
import { recommendationsOffsetForRowAtom } from "@/features/recommendations/recommendations-offset-for-row.atom";
import {
  actionRecommendationsSetOffsetColumn,
  recommendationsAtom,
} from "@/features/recommendations/recommendations.atom";
import * as styles from "@/features/recommendations/recommendations.css";
import { typography } from "@/styles/typography.css";

import { VirtualListRowItem } from "./virtual-list-row-item";
import type { ChannelResult, ProgramResult } from "@sunrise/backend-types";

type VirtualListRowProps = CommonProps & {
  result:
    | (Omit<ChannelResult, "title"> & { title: string | null })
    | (Omit<ProgramResult, "title"> & { title: string | null });
  idx: number;
  focusedIndex: number | null;
  size: number;
  start: number;
};

export const VirtualListRow = memo(function VirtualListRow({
  "data-testid": dataTestId = "virtual-list-row",
  focusedIndex,
  ...props
}: VirtualListRowProps): ReactElement {
  const parentRef = useRef<HTMLDivElement>(null);

  const kind = props.result.kind;
  const dispatchRecommendations = useSetAtom(recommendationsAtom);

  const virtualizer = useVirtualizer({
    overscan: 1,
    count: props.result.items.length,
    horizontal: true,
    initialOffset: useAtomValue(recommendationsOffsetForRowAtom(props.idx)),
    initialRect: {
      width: SCREEN_WIDTH_IN_PX,
      height: recommendationRowSize[kind].height,
    },
    getScrollElement: () => parentRef.current,
    estimateSize: () => programBoxSize[kind].width,
  });

  const disableAnimations = useAtomValue(disableAnimationsAtom);
  useEffect(() => {
    if (isNil(focusedIndex)) return;

    const [offset] = virtualizer.getOffsetForIndex(focusedIndex, "center");
    parentRef.current?.scrollTo({
      left: offset,
      behavior: disableAnimations ? "auto" : "smooth",
    });

    dispatchRecommendations(
      actionRecommendationsSetOffsetColumn(props.idx, offset),
    );
  }, [
    focusedIndex,
    virtualizer,
    disableAnimations,
    dispatchRecommendations,
    props.idx,
  ]);

  return (
    <div
      style={{
        height: `${props.size}px`,
        transform: `translateY(${props.start}px)`,
        willChange: "transform",
      }}
      className={props.className}
    >
      <div
        data-testid={`${dataTestId}.${props.idx}.title`}
        className={clsx([typography.h5.regular, styles.rowTitle])}
      >
        {props.result.title}
      </div>
      <div
        data-testid={`${dataTestId}.${props.idx}`}
        className={clsx([styles.row, props.className])}
      >
        {/* The scrollable element for the list */}
        <div ref={parentRef} className={styles.scrollableRow}>
          {/* The large inner element to hold all of the items */}
          <div
            className={styles.rowItems}
            style={{
              width: `${virtualizer.getTotalSize()}px`,
            }}
          >
            {/* Only the visible items in the virtualizer, manually positioned to be in view */}
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const maybeItem = props.result.items[virtualItem.index];
              if (isNil(maybeItem)) throw new Error("row is undefined");
              const isFocused = virtualItem.index === focusedIndex;

              return (
                <VirtualListRowItem
                  virtualItem={virtualItem}
                  key={virtualItem.index}
                  item={maybeItem}
                  isFocused={isFocused}
                  index={props.idx}
                  kind={kind}
                  data-testid={dataTestId}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});
